import React, { useCallback, useState } from 'react';

import { FlatList, ListRenderItemInfo, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { Edge, SafeAreaView } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';

import { RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectLayoutConfig } from 'store/config/selectors';
import { changeUserProfileAvatar } from 'store/user/actions';

import useBackHandler from 'utils/hooks/useBackHandler.ts';
import useHeaderConfig from 'utils/hooks/useHeaderConfig';
import useSafeAreaEdges from 'utils/hooks/useSafeAreaEdges';
import { isTabletOrWeb, isWeb } from 'utils/responsiveness';

import WebHeader from 'screens/Settings/components/WebHeader';

import ProfileAvatar from '../components/ProfileAvatar';
import { KIDS_ICONS_LIST, STANDARD_ICONS_LIST } from './constants';
import styles from './styles';

type Props = {
    navigation: StackNavigationProp<
        RouteNavigationParams,
        SCREEN.PROFILE_AVATARS
    >;
    route: RouteProp<RouteNavigationParams, SCREEN.PROFILE_AVATARS>;
};

const {
    PROFILE: { PROFILE_AVATAR_COLLECTION_TITLE },
} = LANGUAGE_KEYS;

const ProfileAvatarList = ({
    navigation,
    route: {
        params: { isChildModeEnabled },
    },
}: Props) => {
    const [isChildProfile] = useState(
        typeof isChildModeEnabled === 'string'
            ? JSON.parse(isChildModeEnabled)
            : isChildModeEnabled,
    );

    useHeaderConfig({
        isRoot: false,
    });

    const edges: Edge[] = useSafeAreaEdges();

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { background } = useSelector(selectLayoutConfig);

    const handleAvatarSelection = useCallback(
        (iconId: string) => {
            dispatch(changeUserProfileAvatar(iconId));
            navigation.goBack();
        },
        [navigation],
    );

    const renderAvatarItem = useCallback(
        ({ item, index }: ListRenderItemInfo<string>) =>
            item ? (
                <ProfileAvatar
                    iconId={item}
                    onPress={handleAvatarSelection}
                    index={index}
                    key={item}
                />
            ) : (
                <View style={gStyles.flex} key={item} />
            ),
        [handleAvatarSelection],
    );

    const handleHardwareBackPress = useCallback(() => false, []);

    useBackHandler(handleHardwareBackPress);

    return (
        <SafeAreaView
            style={[gStyles.flex, { backgroundColor: background }]}
            edges={edges}
        >
            {isWeb && (
                <WebHeader
                    title={t(PROFILE_AVATAR_COLLECTION_TITLE)}
                    style={{ backgroundColor: background }}
                />
            )}
            <FlatList
                data={isChildProfile ? KIDS_ICONS_LIST : STANDARD_ICONS_LIST}
                renderItem={renderAvatarItem}
                numColumns={isTabletOrWeb ? 6 : 3}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={styles.contentContainer}
            />
        </SafeAreaView>
    );
};

export default ProfileAvatarList;
